<template lang="pug">
  .hotdeal(v-if="!timesUp")
    .bar(v-if="showBar")
      div(v-if="layout === 'category' || layout === 'carousel'" :class="[layout === 'carousel' ? 'carousel-styles' : 'condensed']")
        .deal-upcoming(v-if="upcoming && Object.keys(time).length > 0")
          .text.upcoming {{ __('Upcoming Deal In: ')}}
          .text.upcoming-time(v-if="hydrated") {{pad(time.days)}}:{{pad(time.hrs)}}:{{pad(time.mins)}}:{{pad(time.secs)}}
          .text.upcoming-time(v-else) {{'--'}}:{{'--'}}:{{'--'}}:{{'--'}}
        .regular-deal(v-else)
          .text(v-if="type === 'qty'") {{ barWidth }}% {{ __('Left')}}
          .text(v-else-if="Object.keys(time).length > 0 && hydrated") {{pad(time.days)}}:{{pad(time.hrs)}}:{{pad(time.mins)}}:{{pad(time.secs)}}
          .text(v-else-if="Object.keys(time).length > 0") {{'--'}}:{{'--'}}:{{'--'}}:{{'--'}}
        .bar-background(v-if="!upcoming")
          .remaining(:style="{width: barWidth+'%'}")
      .full(v-else-if="layout === 'rhpa'")
        .time(v-if="Object.keys(time).length > 0")
          | {{__(upcoming ? 'Upcoming Deal In:' : 'Time Remaining:')}}
          .clock(:class="smallTimer ? 'small-timer' : ''")
            .val
              .num(v-if="hydrated") {{ time.days }}
              .num(v-else) {{ '--' }}
              .text(v-if="hydrated") {{ time.days === 1 ? __('Day') : __('Days') }}
              .text(v-else) {{ __('Hrs') }}
            .val
              .num(v-if="hydrated") {{ time.hrs }}
              .num(v-else) {{ '--' }}
              .text(v-if="hydrated") {{ time.hrs === 1 ? __('Hr') : __('Hrs') }}
              .text(v-else) {{ __('Hrs') }}
            .ellipsis :
            .val
              .num(v-if="hydrated") {{ time.mins }}
              .num(v-else) {{ '--' }}
              .text(v-if="hydrated") {{ time.mins === 1 ? __('Min') : __('Mins') }}
              .text(v-else) {{ __('Mins') }}
            .ellipsis :
            .val
              .num(v-if="hydrated") {{ time.secs }}
              .num(v-else) {{ '--' }}
              .text(v-if="hydrated") {{ time.secs === 1 ? __('Sec') : __('Secs') }}
              .text(v-else) {{ __('Secs') }}
        .qty(v-if="type === 'qty' || promo.qtyPctRemaining")
          | {{type === 'qty' ? __('Quantity Remaining at Hot Deal Price:') : __('Percent Remaining at Hot Deal price')}}
          .qty-remain {{ (type === 'qty' && promo.qty !== undefined) ? promo.qty : (promoPctRemaining(promo.qtyPctRemaining ?? 0) + '%') }}
          .bar-background
            .remaining(:style="{width: barWidth+'%'}")
    .price-compare(:class="showBar && 'top-line'" v-if="layout === 'rhpa' && price.normal && price.normal !== price.hot")
      .left
        .normal
          | {{__('Normal Price')}}
          .price {{$rogue.currency(price.normal, { euTaxClass: true })}}
        .excluding-vat
          Price(
            :showExclVat="false"
            :onlyExclVat="true"
            :hide-normal-price="true"
            :buyRequest="buyRequest"
            :showTotalPrice="false"
            theme="rhpa"
          )
      .hot
        HotDealBadge.hot-img(:label="__('Hot Deal')")
        .price {{$rogue.currency(price.hot, { euTaxClass: true })}}
</template>
<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, reactive, watch, defineAsyncComponent } from 'vue'
import { getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import { useProductStore } from '@/js/stores/productStore'
import { usePromoStore } from '@/js/stores/promoStore'
import { computePriceSync } from '@/js/helpers/price-utils'
import { lowestRemainingPromo, promoPctRemaining } from '@/js/helpers/promo-price'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest } from '@/js/services/RogueTypes'

// Async so the badge loads in its own chunk rather than being bundled with another importer's chunk group
const HotDealBadge = defineAsyncComponent(() => import('sisu/components/core/HotDealBadge.vue'))

const props = withDefaults(defineProps<{
  productId?: number | string
  buyRequest?: BuyRequest
  layout?: string // category, rhpa, carousel
}>(), {
  layout: 'category',
})

// Services/stores
const rogue = useRogue()
const productStore = useProductStore()
const promoStore = usePromoStore()

const ts = ref<number>(Date.now())
const hydrated = ref(false)
const price = reactive<{ normal: number | null; hot: number | null }>({
  normal : null,
  hot    : null,
})
let intervalId: number | undefined

const products = computed(() => productStore.products)
const activePromos = computed(() => promoStore.activePromos)
// confirm this props.productId != null ?
const ownPromo = computed(() => activePromos.value[(props.buyRequest ? getSimpleForBuyRequest(props.buyRequest, rogue)?.productId : props.productId) as number])
// The children a feed parent entry stands for. It lists only the children feeding its price; the rest name the
// parent through dealsParentId.
function parentChildIds(productId: string | number | undefined): number[] {
  const parent = activePromos.value[Number(productId)]
  if (!parent?.isParent) {
    return []
  }

  return [...(parent.childrenIds ?? []), ...(promoStore.childPromoIdsByParent[parent.productId] ?? [])]
}
// The children whose deals the bar picks from. A group buy request names them directly, but a configurable child
// resolves only to its selected simple, so the feed's parent entry fills in the siblings. Everything else reads the
// parent entry alone, by id or by buy request, and a single-child grouped PDP names its group only in metadata.
const childIds = computed<number[]>(() => {
  const { buyRequest } = props
  if (buyRequest?.type === 'group') {
    const selected = buyRequest.children?.map(child => getSimpleForBuyRequest(child, rogue)?.productId) ?? []

    return [...new Set([...selected, ...parentChildIds(buyRequest.productId)])]
  }
  if (buyRequest?.type === 'bundle') {
    return []
  }

  return [...new Set([
    ...parentChildIds(buyRequest ? buyRequest.productId : props.productId),
    ...parentChildIds(buyRequest?.metadata?.groupedProductId),
  ])]
})
// The bar tracks the child closest to selling out, falling back to the product's own entry when no child qualifies
const promo = computed(() => lowestRemainingPromo(childIds.value.map(id => activePromos.value[id])) ?? ownPromo.value)
const upcoming = computed(() => (promo.value?.status === 'upcoming'))
const type = computed<'qty' | 'time'>(() =>
  promo.value?.qtyPctRemaining || (promo.value?.qty !== undefined && (promo.value?.qtyStart ?? 0) > 0)
    ? 'qty'
    : 'time'
)
const showBar = computed(() => {
  if (props.buyRequest?.type === 'group' && price.normal === price.hot && (price.normal ?? 0) > 0) {
    return false
  }

  return promo.value?.qtyPctRemaining || (type.value === 'qty' && promo.value?.qtyStart) || (promo.value?.start && promo.value?.end) || upcoming.value
})
const product = computed(() => (props.productId != null ? products.value[props.productId as number] : undefined))
const barWidth = computed(() => {
  if (!promo.value) return 0

  if (type.value === 'qty' && promo.value.qtyStart) {
    return promoPctRemaining((promo.value.qty ?? 0) / promo.value.qtyStart)
  }

  if (promo.value.qtyPctRemaining) {
    return promoPctRemaining(promo.value.qtyPctRemaining)
  }

  const start = new Date(promo.value.start as any)
  const end = new Date(promo.value.end as any)
  const duration = end.getTime() - start.getTime()
  const elapsed = Date.now() - start.getTime()
  if (duration <= 0) return 0

  return Math.round(100 * (1 - elapsed / duration))
})

const time = computed(() => {
  const p = promo.value
  if (!p) return {}
  if (!upcoming.value && !(p.start && p.end)) return {}

  const target = upcoming.value ? p.start : p.end
  const now = new Date(ts.value)
  const deltaInt = new Date(target as any).getTime() - now.getTime()

  if (deltaInt < 0) {
    return deltaInt
  }

  let delta = deltaInt / 1000
  const days = Math.floor(delta / 86400); delta -= days * 86400
  const hrs  = Math.floor(delta / 3600) % 24; delta -= hrs * 3600
  const mins = Math.floor(delta / 60) % 60; delta -= mins * 60
  const secs = Math.floor(delta % 60)

  if (secs <= 0 && mins <= 0 && hrs <= 0 && days <= 0) {
    refreshPromos()

    return {}
  }

  return { days, hrs, mins, secs }
})

const smallTimer = computed(() => {
  let st: boolean | undefined
  if (['EUR', 'GBR'].includes(rogue.site.code) && rogue.l10n.lang !== 'en') {
    st = true
  }

  return st
})

const timesUp = computed(() => {
  return typeof time.value === 'number' && time.value < 0 ? true : false
})

watch(
  () => props.buyRequest,
  () => {
    if (!props.buyRequest) {
      price.hot = null
      price.normal = null

      return
    }
    try {
      price.hot = computePriceSync(props.buyRequest, rogue.pinia!)
      price.normal = computePriceSync(props.buyRequest, rogue.pinia!, { ignorePromo: true })
    } catch (e) {
      rogue.log.info('Error computing hotdeal price', e as Error)
    }
  },
  { deep: true, immediate: true }
)
// lifecycle
onMounted(() => {
  hydrated.value = true
  intervalId = window.setInterval(() => {
    ts.value = Date.now()
  }, 1000)
})

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId)
})

// methods (as plain functions)
function pad(num: number) {
  return num < 10 ? `0${num}` : String(num)
}

async function refreshPromos() {
  await promoStore.refresh(rogue)
}


</script>
<style lang="scss" scoped>

.carousel-styles {
  font-size: $size-xxs;
  font-weight: $weight-bold;
  margin-top: 5px;
  display: flex;
  justify-content: center;
  align-items: center;

  .text {
    margin-right: 5px;
  }
}

.condensed {
  display: flex;
  align-items: center;
  margin-bottom: $space-xs;

  @include mobile() {
    display: block;
  }

  .deal-upcoming, .regular-deal {
    display: flex;
  }

  .text {
    color: $color-black-light;
    text-decoration: none;
    font-size: $size-xxs;
    font-weight: $weight-bold;
    margin-right: $space-xs;

    &.upcoming {
      color: $color-gray-dark;
      font-weight: normal;
    }

    &.upcoming-time {
      color: $color-green-light;
    }

  }

  .bar-background {
    height: 6px;
  }
}

.bar-background {
  flex-grow: 1;
  height: 12px;
  background-color: $color-gray;
  border-radius: 3px;
}

.remaining {
  border-radius: 3px;
  background-color: $color-green;
  height: 100%;
}

.time {
  align-items: center;
  display: flex;
  margin-bottom: $space-s;
}

.clock {
  margin-left: $space-s;
  display: flex;
  border: $border-width-thick solid $color-black-light;
  font-family: $font-condensed;
  padding: 0 4px;

  .ellipsis {
    //margin-left: -4px;
    font-size: $size-xl;
    line-height: 33px;
  }

  .val {
    width: 42px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: $space-xxxs;
    font-weight: bold;
    text-transform: uppercase;

    .num {
      font-size: $size-xl;
    }

    .text {
      font-size: $size-xs;
    }
  }
}

.clock.small-timer .text {
  font-size: 10px;
}

.qty {
  font-size: $size-xs;
  margin-bottom: $space-s;
}

.qty-remain {
  margin: $space-xxs 0;
  font-weight: $weight-bold;
  font-family: $font-condensed;
  font-size: $size-xl;
}

.price-compare {
  display: flex;
  justify-content: space-between;

  .left {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
  }

  &.top-line {
    border-top: 1px solid $color-gray;
    padding-top: $space-s;
  }

  margin-bottom: $space-s;

  .price {
    margin-top: 4px;
    font-weight: bold;
    font-size: $size-xl;
    font-family: $font-condensed;
  }

  .normal .price {
    text-decoration: line-through;
    color: $color-black-light;
  }

  // The badge artwork has padding around its letters, so it runs taller than the old logo to keep the same letter size
  .hot-img {
    height: 18px;
    width: auto;
    margin-left: auto;
  }

  .hot {
    text-align: right;
  }
}
</style>
